// PropertyEnrichCard — Auto-fill card that pulls property stats from
// Philly OPA + AI rent estimate (Claude Sonnet 4.5). Used on Unit Detail
// pages and any surface where the office wants to seed listing meta from
// public sources without typing.
//
// Props:
//   address (string, required)
//   unit    (string, optional — used for the apply lookup + rent context)
//   extraNotes (string, optional — hint for Claude)
//   onApplied (fn, optional — called after successful apply)
//   dense (bool, optional — compact styling for sidebars)
import { useState } from "react";
import { toast } from "sonner";
import { Globe, CheckCircle, Warning, Sparkle } from "@phosphor-icons/react";
import { api, formatApiErrorDetail } from "@/lib/api";

export default function PropertyEnrichCard({ address, unit, extraNotes = "", onApplied, dense = false }) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [applying, setApplying] = useState(false);

  async function fetchEnrich() {
    setBusy(true); setResult(null);
    try {
      // Pass `unit` explicitly so the rent estimate is per-unit for
      // multi-family — otherwise a 12bd building returns $7,200/mo total
      // instead of the ~$600–700/mo per bedroom that actually applies.
      const r = await api.post("/property-enrich/full", {
        address, unit: unit || null, extra_notes: extraNotes,
      });
      setResult(r.data);
      const src = r.data.sources || [];
      if (!src.length) toast.error("No data found");
      else {
        const parts = [];
        if (src.includes("philly_opa")) parts.push("Philly OPA facts");
        if (src.includes("claude_sonnet_4_5")) parts.push("AI rent estimate");
        toast.success(`Pulled ${parts.join(" + ")}`);
      }
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Fetch failed")); }
    finally { setBusy(false); }
  }

  async function apply() {
    if (!result) return;
    const fields = {};
    if (result.opa) {
      if (result.opa.listing_bedrooms != null) fields.listing_bedrooms = result.opa.listing_bedrooms;
      if (result.opa.listing_bathrooms != null) fields.listing_bathrooms = result.opa.listing_bathrooms;
      if (result.opa.listing_sqft) fields.listing_sqft = result.opa.listing_sqft;
      if (result.opa.year_built) fields.year_built = result.opa.year_built;
      if (result.opa.lot_size_sqft) fields.lot_size_sqft = result.opa.lot_size_sqft;
      if (result.opa.property_type) fields.property_type = result.opa.property_type;
      if (result.opa.zip_code) fields.zip_code = result.opa.zip_code;
      if (result.opa.market_value_cents) fields.market_value_cents = result.opa.market_value_cents;
    }
    if (result.rent) {
      if (result.rent.listing_rent_cents) fields.listing_rent_cents = result.rent.listing_rent_cents;
      if (result.rent.neighborhood) fields.listing_neighborhood = result.rent.neighborhood;
      // Persist AI insight so the rent chip can explain itself later
      // without a fresh Claude call (fast, and doesn't burn tokens).
      fields.listing_rent_source = "claude_sonnet_4_5";
      if (result.rent.rationale) fields.listing_rent_rationale = result.rent.rationale;
      if (result.rent.range_low)  fields.listing_rent_range_low_cents  = result.rent.range_low * 100;
      if (result.rent.range_high) fields.listing_rent_range_high_cents = result.rent.range_high * 100;
      if (result.rent.confidence) fields.listing_rent_confidence = result.rent.confidence;
      fields.listing_rent_updated_at = new Date().toISOString();
    }
    if (!Object.keys(fields).length) { toast.error("Nothing to apply"); return; }
    setApplying(true);
    try {
      await api.post("/property-enrich/apply", { address, unit, fields });
      toast.success(`Applied ${Object.keys(fields).length} field${Object.keys(fields).length === 1 ? "" : "s"} to unit`);
      onApplied && onApplied();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Apply failed")); }
    finally { setApplying(false); }
  }

  return (
    <div className={`border-2 border-dashed border-cyan-500 bg-cyan-50 ${dense ? "p-3" : "p-4"}`} data-testid="property-enrich-card">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="text-[10px] font-black uppercase tracking-widest text-cyan-700 flex items-center gap-1">
            <Sparkle weight="fill" size={12}/> Auto-fill from web
          </div>
          <div className="text-xs text-slate-600 mt-0.5">Philly OPA (city assessment) + AI rent comps — free, no API keys.</div>
        </div>
        <button onClick={fetchEnrich} disabled={busy}
          className="shrink-0 h-9 px-3 bg-cyan-600 hover:bg-cyan-700 disabled:opacity-40 text-white font-black uppercase text-[10px] tracking-widest inline-flex items-center gap-1"
          data-testid="property-enrich-fetch-btn">
          <Globe size={12}/> {busy ? "Fetching…" : "Pull from web"}
        </button>
      </div>

      {result && (
        <div className="mt-3 space-y-2 text-[11px]" data-testid="property-enrich-result">
          {result.opa ? (
            <div className="bg-white border border-cyan-200 rounded p-2">
              <div className="flex items-center gap-1 font-black text-cyan-800">
                <CheckCircle weight="fill" size={12}/> Philly OPA
                <span className="font-normal text-slate-500 truncate ml-1">— {result.opa.matched_address}</span>
              </div>
              <div className="text-slate-700 mt-0.5">
                {result.opa.listing_bedrooms != null && <span>{result.opa.listing_bedrooms}bd · </span>}
                {result.opa.listing_bathrooms != null && <span>{result.opa.listing_bathrooms}ba · </span>}
                {result.opa.listing_sqft && <span>{result.opa.listing_sqft.toLocaleString()} sqft · </span>}
                {result.opa.year_built && <span>built {result.opa.year_built} · </span>}
                {result.opa.property_type}
              </div>
              {result.opa.market_value_cents && (
                <div className="text-slate-500">Assessed ${(result.opa.market_value_cents / 100).toLocaleString()}</div>
              )}
            </div>
          ) : result.opa_error ? (
            <div className="text-red-600 flex items-center gap-1"><Warning size={12}/> OPA: {result.opa_error}</div>
          ) : (
            <div className="text-slate-500 italic">OPA had no match — check the address spelling</div>
          )}

          {result.rent ? (
            <div className="bg-white border border-emerald-200 rounded p-2">
              <div className="font-black text-emerald-800">
                <CheckCircle weight="fill" size={12} className="inline mr-1"/>
                Rent estimate: ${result.rent.suggested_rent?.toLocaleString()}/mo
                <span className="text-slate-500 font-normal"> · ${result.rent.range_low?.toLocaleString()}–${result.rent.range_high?.toLocaleString()}</span>
              </div>
              <div className="text-slate-600 italic mt-0.5">{result.rent.rationale}</div>
              <div className="text-[10px] text-slate-500 mt-0.5">
                {result.rent.neighborhood} · confidence: <span className="font-bold">{result.rent.confidence}</span>
              </div>
            </div>
          ) : result.rent_error ? (
            <div className="text-red-600 flex items-center gap-1"><Warning size={12}/> Rent: {result.rent_error}</div>
          ) : null}

          <button onClick={apply} disabled={applying}
            className="w-full h-9 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 text-white font-black uppercase text-[10px] tracking-widest"
            data-testid="property-enrich-apply-btn">
            {applying ? "Applying…" : "Apply to this unit"}
          </button>
        </div>
      )}
    </div>
  );
}
